import { create } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import { isEqual } from 'lodash';
import { createActiveFolderModel } from './activeFolderModel';
import { createPricingSummaryModel } from './pricingSummaryModel';

/**
 * Global TO-app store.
 *
 * Modular pattern (mirrors apps/dmc/src/store): each domain slice is a model
 * creator combined here. Persisted to localStorage so the active folder
 * survives page reloads.
 */
export const useKoobStore = create(
  devtools(
    persist(
      (...args) => ({
        ...createActiveFolderModel(...args),
        ...createPricingSummaryModel(...args),
      }),
      {
        name: 'koob-to-store',
        storage: createJSONStorage(() => localStorage),
        // Only persist serializable folder identity; transient flags are not persisted.
        partialize: state => ({ activeFolder: state.activeFolder }),
      },
    ),
    { name: 'KoobToStore' },
  ),
);

// ============ Selectors ============

// Read the currently active client folder (null when none).
export const useActiveFolder = () => useKoobStore(state => state.activeFolder);

// Read the refreshing flag for the active folder.
export const useIsFolderRefreshing = () =>
  useKoobStore(state => state.isFolderRefreshing);

// ============ Selectors Pricing Summary ============
export const usePricingSummary = () =>
  useKoobStore(state => state.pricingSummary);

export const usePricingServices = () =>
  useKoobStore(state => state.pricingSummary?.services ?? []);

export const usePricingAdditionalInformations = () =>
  useKoobStore(state => state.pricingSummary?.additionalInformations ?? []);

export const useIsPricingSummaryLoading = () =>
  useKoobStore(state => state.isPricingSummaryLoading);

export const useIsPricingSummarySaving = () =>
  useKoobStore(state => state.isPricingSummarySaving);

// True once the working summary differs from the last loaded/saved snapshot.
export const useIsPricingSummaryDirty = () =>
  useKoobStore(
    state => !isEqual(state.pricingSummary, state.initialPricingSummary),
  );

export const useGlobalMarkup = () =>
  useKoobStore(state => ({
    value: state.pricingSummary?.markup ?? 0,
    isPercent: state.pricingSummary?.isMarkupPercent ?? true,
  }));

export const usePricingDisplayMode = () =>
  useKoobStore(state => state.pricingSummary?.displayMode ?? 'itemized');

export const useTravelDiaryPriceDisplay = () =>
  useKoobStore(state => ({
    isTravelDiaryPriceDisplayed:
      state.pricingSummary?.isTravelDiaryPriceDisplayed ?? false,
    travelDiaryPriceDisplayLevel:
      state.pricingSummary?.travelDiaryPriceDisplayLevel ?? null,
  }));
